<template>
  <div v-if="showErrorMessages" class="alert alert-danger">
    {{ $i18n.t(errorMessage) }}
  </div>

  <div v-else-if="form" class="dynform" :class="{ readonly: form.readOnly }">
    <div class="dirty-warning-msg mb-3" v-if="dirty && showIsDirtyWarningMessage">
      <el-alert
        :title="$i18n.t('form_is_dirty_warning_message_title')"
        type="warning"
        :description="$i18n.t('form_is_dirty_warning_message_description')"
        show-icon
        :closable="false"
      />
    </div>
    <form-error :show-messages="showMessages" :in-popup="this.inPopup" />

    <loader v-show="isLoading && isEmpty(cachedHTML)" :active="isLoading" class="sticky sticky-loader" />

    <div v-if="captchaText && !captchaFieldPath">
      <div class="form-message-list">
        <div class="form-error-list">
          <div title="Mentést akadályozó hibák" class="alert d-flex show-all danger-container alert-danger">
            <span aria-hidden="true" class="glyphicon glyphicon-resize-small"></span>
            <h3>{{ $i18n.t('Mentést akadályozó hibák') }}</h3>
          </div>
          <div>
            <div role="alert" class="alert message-container danger-container alert-danger">
              <div class="message-list active">
                <div class="flex-container d-flex">
                  <span class="message-icon"></span>
                  <p class="alert-message"><span>Captcha:</span> {{ $i18n.t(' kötelezően kitöltendő!') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading && !isEmpty(cachedHTML)" v-html="cachedHTML" />

    <base-form-store ref="baseform" v-show="!isLoading" @submit.prevent="frontEndValidation($event)">
      <dyn-form-buttons
        slot="header"
        :is-popup="inPopup"
        :buttons="getButtons"
        :is-spinner-active="isSpinnerActive"
        :form-version-props="formVersionProps"
        :read-only="isReadOnly"
        @button-click="onClickSave($event.event, $event.save)"
      />
      <div slot="buttons" name="problem" class="form-ctrl-btn-group sticky" v-if="Object.keys(getButtons).length">
        <div v-if="!subForm" class="row padded-row">
          <div class="col-xs-12 col-md-8">
            <div class="row">
              <b-button-group class="pager-buttons" v-if="arePagesMultiple">
                <a
                  href="#"
                  @click="setActivePage('-')"
                  :class="{ disabled: !isPrevPagerAvailable }"
                  :title="$i18n.t('form_page_pacer_previous')"
                  class="btn nsbtn btn--border btn--primary btn--animated"
                >
                  <span class="glyphicon glyphicon-menu-left" aria-hidden="true"></span>
                  {{ $i18n.t('form_page_pacer_previous') }}
                </a>
                <a
                  href="#"
                  @click="setActivePage()"
                  :class="{ disabled: !isNextPagerAvailable }"
                  :title="$i18n.t('form_page_pacer_next')"
                  class="btn nsbtn btn--border btn--primary btn--animated"
                >
                  {{ $i18n.t('form_page_pacer_next') }}
                  <span class="glyphicon glyphicon-menu-right" aria-hidden="true"></span>
                </a>
              </b-button-group>
              <span class="text-muted pager-info" v-if="arePagesMultiple"
                >{{ $i18n.t('form_current_page') }}: {{ activePageNumber }}</span
              >
            </div>
          </div>

          <form-version v-if="showFormVersionInfo" :form="form" />
        </div>
      </div>
    </base-form-store>
  </div>
  <div v-else>
    <loader />
  </div>
</template>
<script>
import BaseFormStore from './form/BaseFormStore'
import FormError from './error/FormError'
import Loader from './Loader'
import { refreshList, refreshFields, createForm, prepareExpression, prepareQuery } from '../form'
import FormVersion from './form/FormVersion'

import formStore from '../store/modules/form/index'
import validation from '../utils/mixin-validation'
import confirmation from '../utils/mixin-confirmation'
import notify from '../utils/mixin-notify'
import { mapActions, mapState } from 'vuex'
import { isEmpty, first, get, forEach, cloneDeep, isUndefined, isNull, last, isEqual, reduce, isObject } from 'lodash'

import EventBus from '../utils/event-bus'
import ExpressionResolver from '../store/modules/form/helpers/expression-resolver-store'
import UnhandledError from '../lib/errors/UnhandledError'

import Redirect from '../lib/redirect/Redirect'
import DynFormButtons from './DynFormButtons'

const LOCK_TIMEOUT = 30000

const TYPE_SUBMIT_SAVE = 'save'
const TYPE_SUBMIT_SAVE_AND_NEW = 'saveAndNew'
const TYPE_SUBMIT_SAVE_AND_CLOSE = 'saveAndClose'

const TYPE_INDEX = 'index'
const TYPE_CREATE = 'create'
const TYPE_UPDATE = 'update'

const SUBFORM_ID_COLUMN = {
  id: 'ID',
  hidden: 1,
  type: 'text',
  name: 'ID',
  value: null,
  colClass: 'col-xs-12 col-sm-6 col-md-3 hidden-column',
  labelClass: '',
}

/**
 * @class DynForm
 *
 * @description This component renders and encapsulates the Form
 */
export default {
  name: 'dyn-form',
  mixins: [validation, notify, confirmation],
  mounted() {
    this.subscribeOnStore()
  },
  instanceStore: {
    name: 'formStore',
    module: formStore,
  },
  props: {
    idForm: {
      type: String,
      default: '',
    },
    level: {
      type: Number,
      default: 0,
    },
    externalParams: {
      type: Object,
      default: () => ({}),
    },
    idMainTable: {
      type: [String, Number],
      default: '',
    },
    groupAutoCollapse: {
      type: Boolean,
      default: false,
    },
    collapsedDefaultClose: {
      type: Boolean,
      default: false,
    },
    buttons: {
      type: Object,
      default: () => ({
        save: { event: TYPE_SUBMIT_SAVE, title: 'dynform_button_save' },
        saveAndNew: { event: TYPE_SUBMIT_SAVE_AND_NEW, title: 'dynform_button_saveAndNew' },
        saveAndClose: { event: TYPE_SUBMIT_SAVE_AND_CLOSE, title: 'dynform_button_saveAndClose' },
        close: { event: TYPE_INDEX, title: 'dynform_button_close' },
      }),
    },
    defaultFieldParams: {
      type: Object,
      default: () => ({}),
    },
    subformView: {
      type: String,
      default: '',
    },
    subformGridView: {
      type: Boolean,
      default: false,
    },
    showNotiOnExp: {
      type: Object,
      default: () => ({}),
    },
    usernameHidden: {
      type: Boolean,
      default: false,
    },
    showIsDirtyWarningMessage: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    showHistory: {
      type: Boolean,
      default: false,
    },
    blockchainEnabled: {
      type: Boolean,
      default: false,
    },
    showFormVersionInfo: {
      type: Boolean,
      default: false,
    },
    formDataVersionButtons: {
      type: Object,
      default: () => ({
        showVersionButton: true,
        showCopyButton: false,
        showCloneButton: false,
      }),
    },
    mergeUrlParams: {
      type: Boolean,
      default: false,
    },
    inPopup: {
      type: Boolean,
      default: false,
    },
    popupUrl: {
      type: String,
      default: '',
    },
    subForm: {
      type: Boolean,
      default: false,
    },
    subFormTempData: {
      type: Object,
      default: null,
    },
    saveOnEnter: {
      type: Boolean,
      default: true,
    },
    isReadView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      updates: null,
      cachedHTML: null,
      submitType: null,
      formUrl: '',
      formSaveUrl: '',
      isLoading: true,
      showMessages: false,
      error: false,
      cloning: false,
      idFormDataVersion: null,
      showErrorMessages: false,
      errorMessage: '',
      toggleDropdown: false,
      lockInterval: null,
      saveExpression: {},
      saveExpressionPending: false,
      expressionMessage: undefined,
    }
  },
  computed: {
    ...mapState('popup', ['popups']),
    currentPopup() {
      const popupsLength = get(this.popups, 'length')
      return popupsLength ? this.popups[popupsLength - 1] : null
    },
    isReadOnly() {
      return !!(this.form.readOnly || this.isReadView)
    },
    isActive() {
      return get(this.currentPopup, 'url') === this.url
    },
    isOpened() {
      return (this.popups[this.level] || {}).isOpened
    },
    dirty() {
      return this.$get('dirty')
    },
    form() {
      return this.$get('form')
    },
    formStructure() {
      return this.$get('formStructure')
    },
    formUrlParams: {
      get() {
        return this.formUrl
      },
      set(type) {
        const root = window.location.protocol + '//' + window.location.host

        let path = this.formSaveUrl
        if (type === TYPE_INDEX) {
          path = get(this.formStructure, ['urls', type])
        }
        this.formUrl = path ? `${root}${path}` : this.url
      },
    },
    formVersionsAvailable() {
      return this.idMainTable && this.showHistory
    },
    formVersionProps() {
      return {
        formVersionsAvailable: this.formVersionsAvailable,
        idForm: this.idForm,
        idMainTable: this.idMainTable,
        isBlockchainEnabled: this.blockchainEnabled,
        showVersionButton: this.showVersionButton,
        showCopyButton: this.showCopyButton,
        showCloneButton: this.showCloneButton,
        formDataVersionButtons: this.formDataVersionButtons,
      }
    },
    formCaptcha: {
      get() {
        return this.$get('formCaptcha')
      },
      set(formCaptcha) {
        this.$dispatch('setFormCaptcha', { formCaptcha })
      },
    },
    url() {
      return this.inPopup ? get(this.popups, [this.level, 'url'], '') : ''
    },
    captchaText: {
      get() {
        return this.$get('captchaText')
      },
      set(captchaText) {
        this.$dispatch('setCaptchaText', { captchaText })
      },
    },
    captchaFieldPath() {
      return this.$get('captchaFieldPath')
    },
    captchaValue() {
      return this.$get('captchaValue')
    },
    getButtons() {
      let filteredButtons = Object.entries(this.buttons).filter(([key, value]) => !value.hidden)
      this.evalRedirectUrl(filteredButtons)
      return Object.fromEntries(filteredButtons)
    },
    isSpinnerActive() {
      return this.validateBeforeSave || this.saveExpressionPending
    },
    warningMessages: {
      get() {
        return this.$get('messages').filter((item) => item.type === 'warning')
      },
    },
    errorMessages: {
      get() {
        return this.$get('messages')
      },
      set(messageList) {
        this.$commit('FORM_VEE_VALIDATION', { errors: messageList })
      },
    },
    loader() {
      return this.isLoading ? 'rotate' : 'opacity-pulse'
    },
    isDocumentUploadInProgress() {
      return this.$get('isDocumentUploadInProgress')
    },
    submitRoot() {
      return this.idMainTable ? TYPE_UPDATE : TYPE_CREATE
    },
    isCreateNewElement() {
      return get(this.currentPopup, 'createNewElement.createNewElement', false)
    },
    visiblePages() {
      return this.$get('visiblePages')
    },
    arePagesMultiple() {
      return this.visiblePages.length > 1
    },
    isPrevPagerAvailable() {
      return get(first(this.visiblePages), 'id') !== this.activePageId
    },
    isNextPagerAvailable() {
      return get(last(this.visiblePages), 'id') !== this.activePageId
    },
    activePageId: {
      get() {
        return this.$get('activePageId')
      },
      set(activePageId) {
        this.$dispatch('setActivePageId', { activePageId })
      },
    },
    activePageIndex() {
      return this.visiblePages.findIndex(({ id }) => id === this.activePageId)
    },
    activePageNumber() {
      return this.activePageIndex + 1
    },
    formLock() {
      return this.$get('formLock')
    },
    formReadOnly() {
      return !!(this.form.readOnly || this.formLock.isLocked || this.isReadView)
    },
  },
  methods: {
    prepareQuery,
    prepareExpression,
    isEmpty,
    ...mapActions(['setForceUpdate', 'showModal', 'hideModal']),
    init() {
      const parentModelKeyName = this.$route.query['parentModelKeyName']
      const parentModelKeyValue = this.$route.query['parentModelKeyValue']

      this.$commit('FORM_INIT', {
        idForm: this.idForm,
        idMainTable: this.idMainTable,
        externalParams: Object.assign(this.externalParams, { parentModelKeyName, parentModelKeyValue }),
        showNotiOnExp: this.showNotiOnExp,
        isReadView: this.isReadView,
        showHistory: this.showHistory,
        subformView: this.compatibleSubformView(),
        submitRoot: this.submitRoot,
        usernameHidden: this.usernameHidden,
        groupAutoCollapse: this.groupAutoCollapse,
        collapsedDefaultClose: this.collapsedDefaultClose,
      })
    },
    formDataWithUrlParams({ forms, data }) {
      const result = cloneDeep(data)

      let url
      if (this.inPopup) {
        url = this.popupUrl
      } else {
        const route = get(forms, [0, 'url'])
        url = route ? `${route}/${this.idMainTable ? 'update' : 'create'}` : window.location.pathname
      }

      const searchParams = new URLSearchParams(this.isOpened ? url.split('?')[1] : window.location.search)
      let queryParams = null

      const checkMethod = (url) => {
        const search = /\/(create|update)(\?|$)/.exec(url)
        return search ? search[1] : search
      }
      const method = checkMethod(url)

      queryParams = Object.fromEntries(searchParams.entries())

      if (!['update', 'create'].includes(method) || (method === 'update' && !this.mergeUrlParams)) {
        return result
      }

      forEach(queryParams, (param, key) => {
        queryParams[key] = isNaN(param) ? param : Number(param)
      })

      const itemProps = []
      forEach(forms || {}, (form) => {
        forEach(form.pages || {}, (page) => {
          forEach(page.groups || {}, (group) => {
            forEach(group.items || {}, (item) => {
              if (item.valueFromParam) {
                itemProps.push(item)
              }
            })
          })
        })
      })

      forEach(itemProps, (item) => {
        if (!isNull(queryParams[item.name]) && !isUndefined(queryParams[item.name])) {
          if (result.ITEMS[item.id]) {
            result.ITEMS[item.id].VALUE = queryParams[item.name]
          }
        }
      })

      return result
    },
    async load() {
      this.isLoading = true

      // we only need 'fetchData' when data is in db, else if it's a temporary data, use it instead of fetching the db data
      const dispatchers = ['fetchStructure']
      if (!this.subFormTempData) {
        dispatchers.push('fetchData')
      }
      const dispatcherPromises = dispatchers.map((dispatcher) => {
        return this.$dispatch(dispatcher)
      })

      await Promise.all(dispatcherPromises).catch(() => {
        this.errorMessage = this.$i18n.t('dynform_form_load_error')
        this.showErrorMessages = true
        this.isLoading = false
      })

      if (this.showErrorMessages) return

      const formStructure = this.$get('formStructure')

      const forms = Object.values(formStructure.forms)
      this.setCaptcha(forms)

      if (!isEmpty(this.defaultFieldParams)) {
        this.$commit('OVERWRITE_DATA', this.defaultFieldParams)
      }

      let dataWithUrlparams
      if (this.subFormTempData) {
        dataWithUrlparams = this.subFormTempData
      } else {
        const data = this.$get('formData')
        dataWithUrlparams = this.formDataWithUrlParams({ forms, data })
      }

      await this.$dispatch('formLoad', { formData: createForm(forms, dataWithUrlparams) })
      if (this.idMainTable && !this.cloning) {
        this.$dispatch('lockSet')
      }
      this.saveExpression = formStructure.forms[Object.keys(formStructure.forms)].saveExpression

      this.hiddenDataForms(forms)
      this.formVerification()

      this.isLoading = false
    },
    async loadVersion() {
      const formVersion = await this.getDataVersion()

      const refresh = formVersion.newData.REFRESH
      const forms = Object.values(formVersion.formStructure.forms)
      const actualForm = forms[0]
      const dataWithUrlparams = this.formDataWithUrlParams({ forms, data: refresh })
      const formData = createForm(forms, dataWithUrlparams)
      this.setCaptcha(forms)

      this.hiddenDataForms(forms)
      this.formVerification()

      this.$commit('FORM_INIT', {
        idForm: formVersion.idForm,
        idMainTable: formVersion.idMaintable,
        externalParams: actualForm.externalParams,
      })
      this.$commit('FORM_FETCH_STRUCTURE', formVersion.formStructure)
      this.$commit('FORM_FETCH_DATA', refresh)
      this.$dispatch('formLoad', { formData })
      this.$commit('FORM_SET_VERSION', formVersion.version)
    },
    setActivePage(direction) {
      const index = direction === '-' ? this.activePageIndex - 1 : this.activePageIndex + 1
      this.activePageId = get(this.visiblePages, [index, 'id'])
    },
    async getDataVersion() {
      const parameters = {
        filter: JSON.stringify({
          where: {
            id: this.idFormDataVersion,
          },
        }),
      }
      const response = await this.$client
        .execute({
          operationId: 'FormDataVersions_findOne',
          parameters,
        })
        .catch(() => {
          throw 'not valid form data version'
        })
      return response.body
    },
    async verifyForm() {
      return await this.$dispatch('formVerification')
        .then(() => {
          const problemsCount = this.errorMessages.filter((message) =>
            ['error', 'warning'].includes(message.type)
          ).length
          this.showMessages = problemsCount > 0
        })
        .catch((err) => console.error(err))
    },
    formVerification() {
      get(window, 'window.CORE.params.verification') && this.verifyForm()
    },
    hiddenDataForms(data) {
      let dataConvert
      if (data[0] && data[0].pages) {
        dataConvert = Object.values(data[0].pages)
        this.hiddenFormNumber = dataConvert.findIndex((el) => !el.hiddenExp && !el.hidden)
      }
    },
    saveData() {
      return this.$dispatch('saveData')
    },
    setCaptcha(form) {
      if (form[0].hasCaptcha) {
        this.formCaptcha = form[0].hasCaptcha
      }
    },
    async saveExp(exp) {
      this.saveExpressionPending = true
      const expression = this.prepareExpression(this.prepareQuery(exp.exp))
      try {
        this.expressionMessage = await ExpressionResolver.resolve({
          expression,
          namespace: this.$storeNamespace,
        })
        this.saveExpressionPending = false
      } catch (error) {
        throw new UnhandledError('Expression evaluation error', { exp }, error)
      }
    },
    removeSticky() {
      const problem = document.getElementsByName('problem')
      if (get(problem, '0')) problem[0].classList.remove('sticky')
    },
    reviveSticky() {
      const problem = document.getElementsByName('problem')
      if (get(problem, '0')) problem[0].classList.add('sticky')
    },
    async evalRedirectUrl(buttons) {
      for (let button of buttons) {
        if (!button[1].redirectUrl) continue
        const expressions = button[1].redirectUrl.match(/\$\{(CORE.*?)}/g) // @regex parameterekre matchol a templateben
        if (expressions) {
          for (let expression of expressions) {
            try {
              const expToEval = expression.replace(/\$\{/g, '').replace(/}/g, '') // @regex kiszedi a parameter wrappert
              const result = await ExpressionResolver.resolve({
                expression: expToEval,
                namespace: this.$storeNamespace,
              })
              button[1].redirectUrl = button[1].redirectUrl.replace(expression, result)
            } catch (e) {
              console.error('Fawlty expression in redirectUrl')
              return false
            }
          }
        }
        button[1].redirectUrl =
          button[1].redirectUrl.length && button[1].redirectUrl[0] === '/'
            ? button[1].redirectUrl.substring(1)
            : button[1].redirectUrl
      }
    },
    redirectUrl(button, params) {
      let redirectUrl
      if (button && this.getButtons) redirectUrl = this.getButtons[button].redirectUrl
      if (redirectUrl) {
        const parameters = redirectUrl.match(/\$\{(.*?)}/g) // @regex parameterekre matchol a templateben
        if (parameters) {
          parameters.forEach((item) => {
            const paramName = item.replace(/\$\{|\}/g, '') // Eltávolítja ${} template karaktereket

            if (paramName.startsWith('CORE.params.')) {
              const coreParamName = paramName.replace('CORE.params.', '')
              redirectUrl = redirectUrl.replace(item, CORE.params[coreParamName])
            } else if (paramName.startsWith('CORE.sqlParams.')) {
              const sqlParamName = paramName.replace('CORE.sqlParams.', '')
              redirectUrl = redirectUrl.replace(item, CORE.sqlParams[sqlParamName])
            } else {
              redirectUrl = redirectUrl.replace(item, params[paramName])
            }
          })
        }

        const colonParams = redirectUrl.match(/:([A-Za-z0-9_]+)/g) //match for :PARAMETER
        if (colonParams) {
          colonParams.forEach((item) => {
            const paramName = item.substring(1)
            if (CORE.params[paramName] !== undefined) {
              redirectUrl = redirectUrl.replace(item, CORE.params[paramName])
            }
          })
        }

        if (redirectUrl.startsWith('http:') || redirectUrl.startsWith('https:')) {
          return redirectUrl
        }
        return `/${redirectUrl}`
      }
      return false
    },
    saveExpValidation(popup) {
      return (
        this.saveExpression &&
        this.saveExpression.exp !== '' &&
        this.expressionMessage &&
        this.expressionMessage !== '' &&
        !popup &&
        this.submitType !== TYPE_INDEX
      )
    },
    flattenObject(obj, prefix = '') {
      return reduce(obj, (result, value, key) => {
        const fullPath = prefix ? `${prefix}.${key}` : key
        if (isObject(value) && !isEmpty(value)) {
          Object.assign(result, this.flattenObject(value, fullPath))
        } else {
          result[fullPath] = value
        }
        return result
      }, {})
    },
    async onSubmit($event, popup) {
      if (this.formCaptcha && !this.captchaValue) {
        this.captchaText = true
        return null
      } else {
        this.captchaText = false
        if (this.captchaFieldPath) {
          this.$dispatch('updatePropByPath', { path: this.captchaFieldPath, prop: 'value', value: this.captchaValue })
        }
      }
      if (this.saveExpression && this.submitType !== TYPE_INDEX) {
        await this.saveExp(this.saveExpression)
      }
      if (this.saveExpValidation(popup)) {
        await this.verifyForm()
        if (!this.showMessages) {
          const confirmed = await this._confirm(this.expressionMessage)
          if (!confirmed) return
        }
      }
      if (
        this.veeValidationErrors &&
        typeof this.veeValidationErrors.any == 'function' &&
        this.veeValidationErrors.any()
      ) {
        return false
      } else if (this.submitType === TYPE_INDEX) {
        if (this.isOpened) {
          this.$emit('dynform-close')
          return
        }
        this.formUrlParams = TYPE_INDEX
        location.href = this.redirectUrl('close') || TYPE_INDEX
      } else if (this.inPopup && this.subForm) {
        const subFormRowData = this.form.pages[0].groups[0].rows[0]
        const payload = {
          subFormRowData: [SUBFORM_ID_COLUMN, ...subFormRowData],
          index: get(this.subFormTempData, 'index', null),
        }
        this.$emit('dynform-close', {
          event: $event.event,
          submit: $event.submit,
          payload: cloneDeep(payload),
        })
      } else {
        this.isLoading = true
        this.saveData().then(async (response) => {
          if (!response) return
          const body = get(response, 'BODY')

          if (!isEmpty(body) && body && get(this.currentPopup, 'createNewElement.createNewElement')) {
            const value = get(body, 'idMaintable')
            const { namespace, path } = this.currentPopup.event
            if (value && namespace && path) {
              this.$store.dispatch('setFieldByNamespaceAndPath', { namespace, path, prop: 'value', value })
            }
          }

          const needRedirect = this.needRedirect($event.event)

          if (response.HEADER && response.HEADER.ERROR.length === 0) {
            const payload = {}
            const urls = response.BODY.urls

            if (!needRedirect && this.submitType === TYPE_SUBMIT_SAVE_AND_NEW) {
              this.formSaveUrl = urls[TYPE_CREATE]
              this.formUrlParams = TYPE_CREATE
            }

            const key = Object.keys(body).find((key) => !['idMaintable', 'urls'].includes(key))
            Object.assign(payload, {
              idForm: this.idForm,
              submitType: this.submitType,
              formUrl: this.formUrl,
              records: body[key],
            })

            this.$emit('dynform-saved', payload)
            EventBus.$emit('dynform-saved', payload)

            const responseRedirect = Redirect(response)
            if (responseRedirect) {
              return (window.location.href = responseRedirect)
            }

            const notifications = []
            // ha nincs error, de vannak warning message-ek akkor azokat megjelenítjük
            if (get(response, 'HEADER.WARNING', []).length) {
              this.warningMessages.forEach((warning) => {
                notifications.push({
                  duration: this.$CORE.notificationDuration,
                  title: 'form_save_warning',
                  type: 'warning',
                  pushMessage: true,
                  link: this.formUrl || location.href,
                  text: warning.label ? '${warningText}: ${label}' : '${warningText}',
                  params: {
                    warningText: warning.msg,
                    label: warning.label,
                  },
                })
              })
            }

            const saveNotification = {
              duration: this.$CORE.notificationDuration,
              title: 'saved_successfully',
              type: 'success',
              pushMessage: true,
              params: { formName: this.form.name },
              link: this.formUrl ? this.formUrl : location.href,
            }

            if (!needRedirect && this.inPopup) {
              EventBus.$emit(`refresh-list-data-${this.idForm}`, this.idForm)
              if (get(this.getButtons, [this.submitType, 'showDefaultNotification']) !== false) {
                if (this.submitRoot === TYPE_CREATE) {
                  saveNotification.text = 'new_${formName}_has_been_added'
                } else {
                  saveNotification.text = '${formName}_has_been_updated'
                }

                notifications.push(saveNotification)
                for (const noti of notifications) {
                  await this.showNotification(noti)
                }
                return
              } else {
                return EventBus.$emit('show-notifications')
              }
            }

            if (this.submitRoot === TYPE_CREATE) {
              saveNotification.msg = 'new_${formName}_has_been_added'
            } else {
              saveNotification.msg = '${formName}_has_been_updated'
            }

            notifications.push(saveNotification)
            notifications.map(async (noti) => await this.saveNotification(noti))

            const tableName = get(response, 'BODY.tableName')
            const params = get(response, ['BODY', tableName, 0], {})

            // a BODY-ban visszatero object minden erteket egy flat objectbe rakjuk a pathnak megfelelo kulcs ala
            Object.assign(params, this.flattenObject(get(response, 'BODY', {})))

            const redirectUrl = this.redirectUrl(this.submitType, params)
            if (redirectUrl) {
              return (window.location.href = redirectUrl)
            }

            if (!urls) {
              return window.location.reload()
            }

            switch (this.submitType) {

              case TYPE_SUBMIT_SAVE:
                this.formSaveUrl = urls[TYPE_UPDATE]
                this.formUrlParams = TYPE_UPDATE
                break

              case TYPE_SUBMIT_SAVE_AND_NEW:
                if (this.submitRoot === TYPE_CREATE) {
                  this.setForceUpdate()
                  this.showMessages = true
                } else {
                  this.formSaveUrl = urls[TYPE_CREATE]
                  this.formUrlParams = TYPE_CREATE
                }
                break

              case TYPE_SUBMIT_SAVE_AND_CLOSE:
              case TYPE_INDEX:
                this.formSaveUrl = urls[TYPE_INDEX]
                this.formUrlParams = TYPE_INDEX
                break

            }

            location.href = this.formUrl
          } else {
            EventBus.$emit('captcha-regenerate')
            this.showMessages = true
          }
          this.isLoading = false
        })
      }
    },
    async onClickSave(saveType, submit = false) {
      this.submitType = saveType

      if (this.inPopup && this.isCreateNewElement) {
        this.clearRedirectUrl(saveType)
      }

      if (submit) {
        if (this.isDocumentUploadInProgress) {
          this.showNotification({
            duration: this.$CORE.notificationDuration,
            title: 'form_save_error',
            type: 'error',
            link: this.formUrl ? this.formUrl : location.href,
            html: true,
            text: 'document_upload_in_progress',
            params: {},
          })
        } else {
          await this.onSubmit({ event: saveType, submit })
        }
      } else if (this.inPopup) {
        this.$emit('dynform-close')
      } else if (saveType === TYPE_INDEX && !this.inPopup) {
        this.formUrlParams = TYPE_INDEX
        location.href = this.redirectUrl('close') || TYPE_INDEX
      }
    },
    /**
     * @description Összegyűjti a form mezőit, és indítja a subFormPopupSave eseményt, átadva a form azonosítót és a mezőket tartalmazó listát
     */
    saveSubForm() {
      let items = []
      this.form.pages.forEach((page) => {
        page.groups.forEach((group) => {
          group.rows.forEach((row) => {
            row.forEach((item) => {
              items.push(item)
            })
          })
        })
      })
      EventBus.$emit('subFormPopupSave', this.idForm, items)
    },
    lock() {
      const modelLockTimeout = window.CORE.modelLockTimeout || LOCK_TIMEOUT
      const timer = setInterval(() => {
        if (this.idMainTable && !this.cloning) {
          this.$dispatch('lockSet')
        }
      }, modelLockTimeout)
      window.CORE.intervalTimers.push(timer)
      return timer
    },
    compatibleSubformView() {
      // Backward compatibility
      let sView = 'Default'
      if (this.subformView) {
        sView = this.subformView
      } else if(this.subformGridView) {
         sView = 'Grid'
      }
      return sView
    },
    getDataFromProcess: async function (evt) {
      return await this.$client.execute({
        operationId: 'ProcessBuilder_getProcessContent',
        parameters: {
          processId: evt.processId,
          params: JSON.stringify({
            fileId: evt.fileId,
          }),
        },
      })
    },
    refreshDynform: async function (evt) {
      const isCreateNewElement = get(this.currentPopup, 'createNewElement')
      if (isCreateNewElement) return

      const parameters = {
        idForm: this.idForm,
        idMaintable: this.idMainTable,
        path: get(window.CORE, 'requestRoute', ''),
      }
      let data = await this.$client.execute({
        operationId: 'Dynform_getData',
        parameters,
      })
      let formData = data.body
      const storeStateData = cloneDeep(this.$get('form'))
      if (evt.processId) {
        const response = await this.getDataFromProcess(evt)
        const items = get(response, 'body.data.ITEMS', {})
        formData = { ...formData, ITEMS: items }
        refreshFields(storeStateData, formData)
      }
      if (storeStateData) {
        const form = refreshList(storeStateData, formData)
        refreshFields(storeStateData, formData)
        this.refreshForm(form)
        EventBus.$emit('selection-list', evt)
      }
    },
    refreshForm(form) {
      this.$commit(`FORM_REFRESH`, { form })
    },
    needRedirect(evt) {
      return !!get(this.getButtons, `${evt}.redirectUrl`, false)
    },
    clearRedirectUrl(type) {
      if (this.needRedirect(type)) this.getButtons[type].redirectUrl = ''
    },
    async intervalCheck() {
      setTimeout(() => {
        if (this.updates === null || this.updates > 0) {
          this.intervalCheck()
        } else {
          this.cachedHTML = this.getReducedHTML()
          this.getHTMLPreview()
        }
      }, 1000)
    },
    getReducedHTML() {
      const baseForm = get(this.$refs, 'baseform.$el')
      if (!baseForm) return ''
      const baseFormClone = baseForm.cloneNode(true)
      if (!baseFormClone) return ''
      const subForms = baseFormClone.querySelectorAll('.sub-form-content')
      subForms.forEach((subForm) => {
        const records = subForm.querySelectorAll('.subform-row')
        records.forEach((node, index) => {
          if (index > 0) node.parentElement.removeChild(node)
        })
      })
      return get(baseFormClone, 'innerHTML')
    },
    async getHTMLPreview() {
      const { obj: cachedHTML } = await this.$client.execute({
        operationId: 'Dynform_htmlCache',
        parameters: {
          htmlToCache: this.cachedHTML,
          cacheParams: JSON.stringify({
            externalParams: this.externalParams,
            idForm: this.idForm,
            path: this.path,
          }),
        },
      })
      if (isNull(cachedHTML)) {
        this.intervalCheck()
      } else {
        this.cachedHTML = cachedHTML
      }
    },
    increaseUpdates({ idForm }) {
      if (this.idForm === idForm) this.updates++
    },
    decreaseUpdates({ idForm }) {
      if (this.idForm === idForm) this.updates--
    },
    checkFormChanges(event) {
      if (this.dirty) {
        event.returnValue = this.$i18n.t('you_have_unfinished_changes')
      }
    },
    handleShortcutSave() {
      if (this.isActive) {
        this.onClickSave('save', true)
      }
    },
    removeEventListeners() {
      EventBus.$off('removeSticky')
      EventBus.$off('reviveSticky')
      EventBus.$off('load-data-from-process')
      EventBus.$off('shortcut-save')
      EventBus.$off('update-start')
      EventBus.$off('update-end')

      if (!this.inPopup && this.showIsDirtyWarningMessage)
        window.removeEventListener('beforeunload', this.checkFormChanges)
    },
    addEventListeners() {
      EventBus.$on('removeSticky', this.removeSticky)
      EventBus.$on('reviveSticky', this.reviveSticky)
      EventBus.$on('load-data-from-process', this.refreshDynform)
      EventBus.$on('shortcut-save', this.handleShortcutSave)
      EventBus.$on('update-start', this.increaseUpdates)
      EventBus.$on('update-end', this.decreaseUpdates)

      if (!this.inPopup && this.showIsDirtyWarningMessage)
        window.addEventListener('beforeunload', this.checkFormChanges)
    },
    subscribeOnStore() {
      this.storeSubscribe = this.$store.subscribe((mutation) => {
        if (mutation.type.includes('FORM_SAVE_DATA')) {
          if (!mutation.type.includes(this.$storeNamespace)) {
            this.refreshDynform(mutation.payload.data, mutation)
          }

          const cond = get(mutation, 'payload.data.HEADER.ERROR', []).concat(
            get(mutation, 'payload.data.HEADER.WARNING', [])
          )

          if (cond.length) {
            this.$client
              .execute({
                operationId: 'Notification_readNotifications',
              })
              .then((response) => {
                response.body.forEach((notification) => {
                  this.showNotification({
                    title: notification.title,
                    text: notification.msg,
                    type: notification.type,
                  })
                })
              })
          }
        }
        if (mutation.type === 'SET_FORCE_UPDATE') {
          this.$forceUpdate()
        }
      })
    },
    unsubscribeFromStore() {
      this.storeSubscribe && this.storeSubscribe()
    },
    updateFirstPage() {
      if (!isEqual(first(this.visiblePages), first(get(this.form, 'pages', [])))) {
        this.activePageId = get(first(this.visiblePages), 'id', 'no_page_visible')
      }
    },
  },
  async created() {
    this.cloning = !!this.$route.query['cloning']

    this.$commit('FORM_SET_CLONING', this.cloning)
    this.$commit('FORM_SET_IN_POPUP', this.inPopup)
    this.$commit('FORM_SET_DOCUMENT_UPLOAD_IN_PROGRESS', false)

    if (this.inPopup) {
      this.$commit('FORM_SET_POPUP_URL', this.popupUrl)
    }

    this.idFormDataVersion = this.$route.query['version'] || null
    this.init()
    this.idFormDataVersion ? await this.loadVersion() : await this.load()
    this.updateFirstPage()
    this.lockInterval = this.lock()

    this.addEventListeners()
    this.getHTMLPreview()
  },
  beforeDestroy() {
    clearInterval(this.lockInterval)
    this.removeEventListeners()
    this.unsubscribeFromStore()
  },
  destroyed() {
    this.$store.unregisterModule([this.$storeNamespace])
    delete window.Fields[this.$storeNamespace]
    EventBus.$off('enter-pressed')
  },
  watch: {
    error(e) {
      if (e) throw Error(e)
    },
    dirty(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('dirty-change', newValue)
      }
    },
  },
  components: {
    DynFormButtons,
    BaseFormStore,
    FormError,
    Loader,
    FormVersion,
  },
}
</script>
<style lang="scss" scoped>
@import '../assets/sass/components/_dynform.scss';
.dynform {
  display: block !important;
}
.readonly {
  background: white !important;
  cursor:
    url('../assets/img/notallowed24.png') 2 2,
    not-allowed !important;
}
#form-data-version-list {
  cursor: default;
  pointer-events: all;
}
.padded-row {
  padding-right: 11px;
  padding-left: 11px;
}
.fix-width-btn {
  width: 180px;
}
.fix-width-split {
  width: 145px;
}
.fix-width-caret {
  width: 36px;
}
.glyphicon-save {
  margin-right: 3px;
}
.dirty-warning-msg {
  position: sticky;
  top: 60px;
  z-index: 2000;
}
</style>
